import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";

import useSetCelularWhatsapp from "../../hooks/useSetCelularWhatsapp";
import ContactModal from "../ContactModal";
import { FASE1_CLIENT_CONTACTS_ENABLED } from "../../config/featureFlags";
import { i18n } from "../../translate/i18n";
import { getConflictText } from "../../utils/contactConflict";

const useStyles = makeStyles((theme) => ({
  field: {
    marginBottom: theme.spacing(2),
  },
  hint: {
    marginBottom: theme.spacing(2),
  },
  error: {
    color: theme.palette.error.main,
    marginBottom: theme.spacing(1),
  },
  conflictBox: {
    padding: theme.spacing(1, 0),
  },
}));

/**
 * Mesma regra do helper do backend (validateMobilePhoneInput /
 * normalizeBrazilianPhone): tira máscara, "00" e "55" iniciais (só quando há
 * mais de 11 dígitos) e o zero de discagem; rejeita 0800/0300/0500/0900; DDD
 * 11–99. Celular = 11 dígitos locais com o 9 na frente; 10 dígitos é fixo.
 * A validação de verdade continua sendo a do servidor.
 */
const digitsOnly = (value) => String(value || "").replace(/\D/g, "");
const SERVICE_LINE_PREFIXES = ["0800", "0300", "0500", "0900"];

const normalizeBrazilianPhone = (raw) => {
  let digits = digitsOnly(raw);
  if (!digits) return null;
  if (digits.startsWith("00")) digits = digits.slice(2);
  if (digits.length > 11 && digits.startsWith("55")) digits = digits.slice(2);
  if (SERVICE_LINE_PREFIXES.some((prefix) => digits.startsWith(prefix))) return null;
  if (digits.length > 10 && digits.startsWith("0")) digits = digits.slice(1);
  if (digits.length !== 10 && digits.length !== 11) return null;
  const ddd = Number(digits.slice(0, 2));
  if (!Number.isFinite(ddd) || ddd < 11 || ddd > 99) return null;
  if (digits.length === 11 && digits[2] !== "9") return null;
  return digits;
};

/** { kind: "mobile", local } | { kind: "landline" } | { kind: "invalid" } */
export const classifyCelularInput = (raw) => {
  const local = normalizeBrazilianPhone(raw);
  if (!local) return { kind: "invalid" };
  if (local.length === 10) return { kind: "landline" };
  return { kind: "mobile", local };
};

/**
 * Componente compartilhado "definir celular (WhatsApp)" — usado tanto pelo
 * painel de detalhe da Carteira quanto pelo card do Kanban. Único consumidor
 * de useSetCelularWhatsapp (PUT /contatos/celular). Nunca loga celular/nome
 * no console, mesmo em erro — only toast/helperText, igual ao resto do app.
 *
 * `onSuccess(result)` é chamado com `{ status, contactId, created,
 * linkedExistingContact }` depois de um 200 (inclusive depois de resolver um
 * conflito). Quem chama decide como refletir isso na tela (recarregar
 * detalhe da Carteira, recarregar o board do Kanban) — este componente não
 * assume o formato de contato de cada tela.
 */
const SetCelularModal = ({
  open,
  onClose,
  cpfCnpj,
  policyId,
  initialCelular,
  initialName,
  showNameField = true,
  onSuccess,
}) => {
  const classes = useStyles();
  const { submit, resolveConflict, cancelConflict, reset, saving, errorMessage, conflict, contactConflict } =
    useSetCelularWhatsapp({ cpfCnpj, policyId });

  const [celular, setCelular] = useState("");
  const [name, setName] = useState("");
  const [touched, setTouched] = useState(false);
  // Abre o cadastro (ContactModal) do contato dono do número em conflito,
  // só para consulta/edição dele — não interfere no fluxo de vincular/criar
  // deste modal, que continua funcionando por trás.
  const [existingContactModalOpen, setExistingContactModalOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setCelular(digitsOnly(initialCelular));
      setName(initialName || "");
      setTouched(false);
      reset();
    }
    // reset só deve rodar na abertura, não a cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialCelular, initialName]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const classification = classifyCelularInput(celular);
  const celularValido = classification.kind === "mobile";
  const celularErro =
    classification.kind === "landline"
      ? i18n.t("contactConflict.landline")
      : "Informe DDD + telefone (ex: 48 99999-9999)";

  // Feedback de sucesso centralizado aqui para os dois consumidores (Carteira
  // e Kanban) não precisarem repetir a mesma lógica de texto.
  const notifySuccess = (result) => {
    if (result.created) {
      toast.success("Contato criado e vinculado.");
    } else if (result.linkedExistingContact) {
      toast.success("Vinculado a cadastro existente.");
    } else {
      toast.success("Celular (WhatsApp) atualizado.");
    }
  };

  const handleSubmit = async () => {
    setTouched(true);
    if (!celularValido) return;
    const result = await submit(classification.local, name.trim() || undefined);
    if (result) {
      notifySuccess(result);
      onSuccess?.(result);
      handleClose();
    }
    // 409 (conflito) e 400 (errorMessage) ficam tratados pelo próprio hook —
    // o diálogo continua aberto mostrando o passo seguinte.
  };

  const handleResolveConflict = async () => {
    const result = await resolveConflict();
    if (result) {
      notifySuccess(result);
      onSuccess?.(result);
      handleClose();
    }
  };

  // "Cancelar" no conflito fecha o diálogo inteiro sem reenviar — não volta
  // para o formulário. Mesmo comportamento de "cancelar" no 400/caminho
  // normal: decisão do usuário de não vincular o cliente ao contato
  // existente.
  const handleCancelConflict = () => {
    cancelConflict();
    handleClose();
  };

  const handleOpenExistingContact = () => {
    setExistingContactModalOpen(true);
  };

  const handleCloseExistingContact = () => {
    setExistingContactModalOpen(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Celular (WhatsApp)</DialogTitle>
      {contactConflict ? (
        <>
          <DialogContent>
            <div className={classes.conflictBox}>
              <Typography variant="body2" color="textSecondary">
                {getConflictText(contactConflict.reason, i18n.t.bind(i18n))}
              </Typography>
              {contactConflict.contactName ? (
                <Typography variant="body2" style={{ marginTop: 8 }}>
                  {i18n.t("contactConflict.existingContact", { name: contactConflict.contactName, interpolation: { escapeValue: false } })}
                </Typography>
              ) : null}
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelConflict} color="primary" variant="contained">
              {i18n.t("contactConflict.close")}
            </Button>
          </DialogActions>
        </>
      ) : conflict ? (
        <>
          <DialogContent>
            <div className={classes.conflictBox}>
              <Typography variant="body2" color="textSecondary">
                {conflict.conflictContactName
                  ? `Este número já está no cadastro de ${conflict.conflictContactName}.`
                  : "Este número já está vinculado a outro cadastro."}{" "}
                {FASE1_CLIENT_CONTACTS_ENABLED && conflict.linkable
                  ? "Você pode vincular este cliente a esse cadastro existente, ou cancelar e informar outro número."
                  : "Informe outro número."}
              </Typography>
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelConflict} disabled={saving}>
              Cancelar
            </Button>
            {conflict.conflictContactId ? (
              <Button onClick={handleOpenExistingContact} disabled={saving}>
                Abrir cadastro existente
              </Button>
            ) : null}
            {FASE1_CLIENT_CONTACTS_ENABLED && conflict.linkable ? (
              <Button
                onClick={handleResolveConflict}
                color="primary"
                variant="contained"
                disabled={saving}
              >
                {saving ? <CircularProgress size={20} /> : "Vincular a contato existente"}
              </Button>
            ) : null}
          </DialogActions>
          <ContactModal
            open={existingContactModalOpen}
            onClose={handleCloseExistingContact}
            contactId={conflict.conflictContactId}
          />
        </>
      ) : (
        <>
          <DialogContent>
            <Typography variant="body2" color="textSecondary" className={classes.hint}>
              Informe o número de WhatsApp do cliente. Se ele ainda não tiver cadastro, um novo é
              criado automaticamente.
            </Typography>

            {errorMessage ? <div className={classes.error}>{errorMessage}</div> : null}

            {showNameField ? (
              <TextField
                id="set-celular-name"
                className={classes.field}
                label="Nome"
                fullWidth
                variant="outlined"
                value={name}
                disabled={saving}
                onChange={(event) => setName(event.target.value)}
              />
            ) : null}

            <TextField
              id="set-celular-number"
              className={classes.field}
              label="Celular (WhatsApp)"
              placeholder="(48) 99999-9999"
              fullWidth
              variant="outlined"
              type="tel"
              value={celular}
              disabled={saving}
              autoFocus
              onChange={(event) => setCelular(digitsOnly(event.target.value))}
              error={touched && !celularValido}
              helperText={touched && !celularValido ? celularErro : " "}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={saving}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} color="primary" variant="contained" disabled={saving}>
              {saving ? <CircularProgress size={20} /> : "Salvar"}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default SetCelularModal;
